import React, { useState, useEffect, useCallback } from 'react';
import { hasPermission } from '../utils/permissionUtils';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/manage-gallery.css';

const ManageGallery = ({ isOpen, onClose, user }) => {
  const { t } = useLanguage();
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeView, setActiveView] = useState('main');
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Filters and search
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    totalPages: 0
  });

  // New item form
  const [newItem, setNewItem] = useState({
    title: '',
    caption: '',
    category: 'general',
    tags: [],
    event_date: '',
    is_featured: false,
    is_public: true
  });

  const canManageGallery = hasPermission(user, 'manage-gallery');

  const fetchGalleryItems = useCallback(async () => {
    if (!canManageGallery) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy,
        sortOrder,
        is_public: 'all' // Show all items in management view
      });

      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (featuredFilter !== 'all') params.append('featured', featuredFilter);
      if (searchText.trim()) params.append('search', searchText.trim());

      const response = await fetch(`${API_BASE}/api/gallery?${params}`);
      
      if (!response.ok) {
        throw new Error(t('gallery.loadError'));
      }

      const data = await response.json();
      setGalleryItems(data.items || []);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching gallery items:', err);
      setError(t('gallery.loadError'));
    } finally {
      setLoading(false);
    }
  }, [canManageGallery, pagination.page, pagination.limit, sortBy, sortOrder, categoryFilter, featuredFilter, searchText]);

  useEffect(() => {
    if (isOpen && canManageGallery) {
      fetchGalleryItems();
    }
  }, [isOpen, fetchGalleryItems]);

  const handleFileUpload = async (file) => {
    if (!file) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/gallery-upload`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Upload error:', error);
      setError(`Upload failed: ${error.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    
    const fileInput = e.target.querySelector('input[type="file"]');
    const file = fileInput?.files[0];
    
    if (!file) {
      setError(t('gallery.selectFileFirst'));
      return;
    }

    if (!newItem.title.trim()) {
      setError(t('gallery.titleRequired'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Upload file first
      const uploadResult = await handleFileUpload(file);
      if (!uploadResult) return;

      // Create gallery item
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/gallery`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newItem,
          image_url: uploadResult.url,
          thumbnail_url: uploadResult.thumbnail_url,
          file_name: uploadResult.original_filename,
          file_size: uploadResult.size,
          mime_type: uploadResult.type,
          uploaded_by: user.id || user.email,
          tags: newItem.tags.filter(tag => tag.trim())
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('gallery.createError'));
      }

      setSuccess(t('gallery.createSuccess'));
      setNewItem({
        title: '',
        caption: '',
        category: 'general',
        tags: [],
        event_date: '',
        is_featured: false,
        is_public: true
      });
      fileInput.value = '';
      setActiveView('main');
      fetchGalleryItems();
    } catch (error) {
      console.error('Error creating gallery item:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateItem = async (id, updates) => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/gallery?id=${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('gallery.updateError'));
      }

      setSuccess(t('gallery.updateSuccess'));
      setEditingItem(null);
      fetchGalleryItems();
    } catch (error) {
      console.error('Error updating gallery item:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteItem = async (id, title) => {
    if (!window.confirm(t('gallery.deleteConfirm', { title }))) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/gallery?id=${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('gallery.deleteError'));
      }

      setSuccess(t('gallery.deleteSuccess'));
      fetchGalleryItems();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'general', label: t('gallery.categories.general') },
    { value: 'concert', label: t('gallery.categories.concert') },
    { value: 'rehearsal', label: t('gallery.categories.rehearsal') },
    { value: 'social', label: t('gallery.categories.social') },
    { value: 'achievements', label: t('gallery.categories.achievements') },
    { value: 'behind-scenes', label: t('gallery.categories.behind-scenes') }
  ];

  if (!isOpen) return null;

  if (!canManageGallery) {
    return (
      <div className="gallery-overlay">
        <div className="gallery-dialog">
          <div className="gallery-header">
            <div className="header-content">
              <div className="header-icon">🖼️</div>
              <div className="header-text">
                <h2>{t('gallery.accessDenied')}</h2>
                <p>{t('gallery.noPermission')}</p>
              </div>
            </div>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="gallery-overlay">
      <div className="gallery-dialog">
        <div className="gallery-header">
          <div className="header-content">
            <div className="header-icon">🖼️</div>
            <div className="header-text">
              <h2>{t('gallery.title')}</h2>
              <p>{t('gallery.description')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="gallery-content">
          {error && (
            <div className="message error-message">
              <span className="message-icon">⚠️</span>
              <span className="message-text">{error}</span>
              <button className="message-close" onClick={() => setError(null)}>×</button>
            </div>
          )}

          {success && (
            <div className="message success-message">
              <span className="message-icon">✅</span>
              <span className="message-text">{success}</span>
              <button className="message-close" onClick={() => setSuccess(null)}>×</button>
            </div>
          )}

          {activeView === 'main' && (
            <>
              <div className="gallery-controls">
                <div className="control-row">
                  <button 
                    className="primary-button"
                    onClick={() => setActiveView('add')}
                    disabled={loading}
                  >
                    <span className="button-icon">📷</span>
                    {t('gallery.addPhoto')}
                  </button>

                  <div className="search-box">
                    <input
                      type="text"
                      placeholder={t('gallery.searchPhotos')}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && fetchGalleryItems()}
                    />
                    <button onClick={fetchGalleryItems}>🔍</button>
                  </div>
                </div>

                <div className="filter-row">
                  <select 
                    value={categoryFilter} 
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <option value="all">{t('gallery.allCategories')}</option>
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>

                  <select 
                    value={featuredFilter} 
                    onChange={(e) => setFeaturedFilter(e.target.value)}
                  >
                    <option value="all">{t('gallery.allPhotos')}</option>
                    <option value="true">{t('gallery.featured')}</option>
                    <option value="false">{t('gallery.unfeature')}</option>
                  </select>

                  <select 
                    value={`${sortBy}-${sortOrder}`} 
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                  >
                    <option value="created_at-desc">{t('gallery.sortOptions.newest')}</option>
                    <option value="created_at-asc">{t('gallery.sortOptions.oldest')}</option>
                    <option value="title-asc">{t('gallery.sortOptions.titleAZ')}</option>
                    <option value="title-desc">{t('gallery.sortOptions.titleZA')}</option>
                    <option value="view_count-desc">{t('gallery.sortOptions.mostViewed')}</option>
                  </select>
                </div>
              </div>

              {loading && <div className="loading-spinner">{t('gallery.loading')}</div>}

              <div className="gallery-grid">
                {galleryItems.map((item) => (
                  <div key={item.id} className="gallery-item">
                    <div className="item-image">
                      <img 
                        src={item.thumbnail_url || item.image_url} 
                        alt={item.title}
                        loading="lazy"
                      />
                      {item.is_featured && <div className="featured-badge">⭐</div>}
                    </div>
                    
                    <div className="item-content">
                      <h3 className="item-title">{item.title}</h3>
                      {item.caption && (
                        <p className="item-caption">{item.caption}</p>
                      )}
                      <div className="item-meta">
                        <span className="item-category">{item.category}</span>
                        <span className="item-date">
                          {item.event_date ? new Date(item.event_date).toLocaleDateString() : t('gallery.noDateSet')}
                        </span>
                        {item.view_count > 0 && (
                          <span className="item-views">👁️ {item.view_count}</span>
                        )}
                      </div>
                      
                      <div className="item-actions">
                        <button 
                          className="edit-button"
                          onClick={() => setEditingItem(item)}
                        >
                          ✏️ {t('gallery.edit')}
                        </button>
                        <button 
                          className="feature-button"
                          onClick={() => handleUpdateItem(item.id, { is_featured: !item.is_featured })}
                        >
                          {item.is_featured ? `⭐ ${t('gallery.unfeature')}` : `⭐ ${t('gallery.feature')}`}
                        </button>
                        <button 
                          className="delete-button"
                          onClick={() => handleDeleteItem(item.id, item.title)}
                        >
                          🗑️ {t('gallery.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {pagination.totalPages > 1 && (
                <div className="pagination">
                  <button 
                    disabled={!pagination.hasPrev || loading}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    {t('gallery.previousPage')}
                  </button>
                  <span>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button 
                    disabled={!pagination.hasNext || loading}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    {t('gallery.nextPage')}
                  </button>
                </div>
              )}
            </>
          )}

          {activeView === 'add' && (
            <div className="add-item-form">
              <div className="form-header">
                <button 
                  className="back-button"
                  onClick={() => setActiveView('main')}
                >
                  ← {t('gallery.back')}
                </button>
                <h3>{t('gallery.addNewPhoto')}</h3>
              </div>

              <form onSubmit={handleCreateItem}>
                <div className="form-group">
                  <label>{t('gallery.selectPhotoFile')} *</label>
                  <input
                    type="file"
                    accept="image/*"
                    required
                    disabled={uploading || loading}
                  />
                  <small>{t('gallery.supportedFormats')}</small>
                </div>

                <div className="form-group">
                  <label>{t('gallery.photoTitle')} *</label>
                  <input
                    type="text"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder={t('gallery.titlePlaceholder')}
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>{t('gallery.caption')}</label>
                  <textarea
                    value={newItem.caption}
                    onChange={(e) => setNewItem({ ...newItem, caption: e.target.value })}
                    placeholder={t('gallery.captionPlaceholder')}
                    rows="3"
                    disabled={loading}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('gallery.category')}</label>
                    <select
                      value={newItem.category}
                      onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                      disabled={loading}
                    >
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>{t('gallery.eventDate')}</label>
                    <input
                      type="date"
                      value={newItem.event_date}
                      onChange={(e) => setNewItem({ ...newItem, event_date: e.target.value })}
                      min="2000-01-01"
                      max="2030-12-31"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('gallery.tags')}</label>
                  <input
                    type="text"
                    value={newItem.tags.join(', ')}
                    onChange={(e) => setNewItem({ 
                      ...newItem, 
                      tags: e.target.value.split(',').map(tag => tag.trim()) 
                    })}
                    placeholder={t('gallery.tagsPlaceholder')}
                    disabled={loading}
                  />
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newItem.is_featured}
                      onChange={(e) => setNewItem({ ...newItem, is_featured: e.target.checked })}
                      disabled={loading}
                    />
                    {t('gallery.isFeatured')}
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      checked={newItem.is_public}
                      onChange={(e) => setNewItem({ ...newItem, is_public: e.target.checked })}
                      disabled={loading}
                    />
                    {t('gallery.isPublic')}
                  </label>
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-button"
                    onClick={() => setActiveView('main')}
                    disabled={loading || uploading}
                  >
                    {t('common.cancel')}
                  </button>
                  <button 
                    type="submit" 
                    className="primary-button"
                    disabled={loading || uploading}
                  >
                    {uploading ? t('gallery.uploading') : loading ? t('gallery.creating') : t('gallery.uploadAndSave')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {editingItem && (
            <div className="edit-item-overlay">
              <div className="edit-item-dialog">
                <div className="edit-header">
                  <h3>{t('gallery.editPhoto', { title: editingItem.title })}</h3>
                  <button className="close-button" onClick={() => setEditingItem(null)}>×</button>
                </div>
                
                <div className="edit-content">
                  <div className="edit-photo-preview">
                    <img 
                      src={editingItem.image_url} 
                      alt={editingItem.title}
                      className="preview-image"
                    />
                    <div className="photo-info">
                      <div className="info-item">
                        <strong>{t('gallery.originalFilename')}</strong> {editingItem.file_name}
                      </div>
                      <div className="info-item">
                        <strong>{t('gallery.uploadDate')}</strong> {new Date(editingItem.created_at).toLocaleDateString()}
                      </div>
                      <div className="info-item">
                        <strong>{t('gallery.uploadedBy')}</strong> {editingItem.uploaded_by}
                      </div>
                    </div>
                  </div>

                  <div className="edit-form-section">
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      const formData = new FormData(e.target);
                      const updates = {
                        title: formData.get('title'),
                        caption: formData.get('caption'),
                        category: formData.get('category'),
                        event_date: formData.get('event_date') || null,
                        is_featured: formData.has('is_featured'),
                        is_public: formData.has('is_public'),
                        tags: formData.get('tags').split(',').map(tag => tag.trim()).filter(tag => tag)
                      };
                      handleUpdateItem(editingItem.id, updates);
                    }}>
                      <div className="form-group">
                        <label>{t('gallery.titleRequired')}</label>
                        <input
                          name="title"
                          defaultValue={editingItem.title}
                          required
                          disabled={loading}
                          placeholder={t('gallery.titlePlaceholder')}
                        />
                      </div>

                      <div className="form-group">
                        <label>{t('gallery.captionLabel')}</label>
                        <textarea
                          name="caption"
                          defaultValue={editingItem.caption || ''}
                          rows="4"
                          disabled={loading}
                          placeholder={t('gallery.captionPlaceholder')}
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label>{t('gallery.categoryLabel')}</label>
                          <select name="category" defaultValue={editingItem.category} disabled={loading}>
                            {categories.map(cat => (
                              <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                          </select>
                        </div>

                        <div className="form-group">
                          <label>{t('gallery.eventDateLabel')}</label>
                          <input
                            name="event_date"
                            type="date"
                            defaultValue={editingItem.event_date ? new Date(editingItem.event_date).toISOString().split('T')[0] : ''}
                            min="2000-01-01"
                            max="2030-12-31"
                            disabled={loading}
                          />
                        </div>
                      </div>

                      <div className="form-group">
                        <label>{t('gallery.tagsLabel')}</label>
                        <input
                          name="tags"
                          defaultValue={editingItem.tags?.join(', ') || ''}
                          disabled={loading}
                          placeholder={t('gallery.tagsPlaceholder')}
                        />
                        <small>{t('gallery.separateTagsCommas')}</small>
                      </div>

                      <div className="checkbox-group">
                        <label className="checkbox-label">
                          <input
                            name="is_featured"
                            type="checkbox"
                            defaultChecked={editingItem.is_featured}
                            disabled={loading}
                          />
                          <span className="checkbox-text">{t('gallery.makeFeatured')}</span>
                        </label>

                        <label className="checkbox-label">
                          <input
                            name="is_public"
                            type="checkbox"
                            defaultChecked={editingItem.is_public}
                            disabled={loading}
                          />
                          <span className="checkbox-text">{t('gallery.makePublic')}</span>
                        </label>
                      </div>

                      <div className="form-actions">
                        <button 
                          type="button" 
                          className="cancel-button"
                          onClick={() => setEditingItem(null)}
                          disabled={loading}
                        >
                          {t('common.cancel')}
                        </button>
                        <button type="submit" className="primary-button" disabled={loading}>
                          {loading ? t('gallery.updating') : t('gallery.saveChanges')}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageGallery;